import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export default function EmailSettings() {
  const [cfg, setCfg] = useState({ host: "smtp.gmail.com", port: 587, username: "", password: "", sender_email: "" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.get("/users/me/email-config").then((r) => {
      if (r.data) setCfg({ ...cfg, ...r.data, password: "" });
      setLoaded(true);
    });
    // eslint-disable-next-line
  }, []);

  const change = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setCfg({ ...cfg, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/users/me/email-config", cfg);
      toast.success("Email config saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    if (!testTo) return toast.error("Enter test recipient");
    setBusy(true);
    try {
      const r = await api.post("/users/me/email-config/test", { to: testTo, subject: "SMTP Test", body: "If you see this, your SMTP works." });
      if (r.data.success) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="email-settings-page">
      <div className="border-b border-rule pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Account · Outgoing email</div>
        <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Email setup.</h1>
        <p className="text-quiet mt-3 text-sm max-w-xl">
          When you schedule an interview or notify a candidate, the platform sends mail using
          <span className="text-ink"> your</span> SMTP credentials. Passwords are encrypted at rest with Fernet.
        </p>
      </div>

      <form onSubmit={save} className="mt-8 bg-white border border-rule p-6 space-y-5 fade-up">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Field label="SMTP host">
              <input data-testid="smtp-host" required value={cfg.host} onChange={change("host")} className={inp} />
            </Field>
          </div>
          <Field label="Port">
            <input data-testid="smtp-port" required type="number" value={cfg.port} onChange={change("port")} className={inp} />
          </Field>
        </div>
        <Field label="Username (typically your email)">
          <input data-testid="smtp-username" required value={cfg.username} onChange={change("username")} className={inp} />
        </Field>
        <Field label="Password / App password">
          <input
            data-testid="smtp-password"
            required={!loaded || !cfg.password ? false : true}
            type="password"
            value={cfg.password}
            onChange={change("password")}
            placeholder={loaded ? "Leave blank to keep existing" : ""}
            className={inp}
          />
        </Field>
        <Field label="Sender email (From:)">
          <input data-testid="smtp-sender" required type="email" value={cfg.sender_email} onChange={change("sender_email")} className={inp} />
        </Field>

        <div className="flex justify-between items-center pt-4 border-t border-rule">
          <div className="text-xs text-quiet">
            For Gmail use port 587 with an{" "}
            <a className="text-klein hover:underline" target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords">
              App Password
            </a>
            .
          </div>
          <button data-testid="save-smtp-btn" type="submit" disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium">
            {busy ? "Saving…" : "Save configuration"}
          </button>
        </div>
      </form>

      <section className="mt-6 bg-white border border-rule p-6">
        <h3 className="font-serif text-2xl text-ink">Send test email</h3>
        <div className="mt-3 flex gap-2">
          <input
            data-testid="test-email-to"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className={`${inp} flex-1`}
          />
          <button data-testid="send-test-email-btn" onClick={test} disabled={busy} className="border border-ink hover:bg-paper px-4 py-2 text-sm">
            Send test
          </button>
        </div>
      </section>
    </div>
  );
}

const inp = "w-full bg-transparent border border-rule px-3 py-2.5 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
